import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AddRounded as AddIcon } from '@material-ui/icons';

import { User } from '../../types/user.type';
import { useFindOrders } from '../../../orders/hooks/get-orders.hook';
import { OrderTableWithDetails } from '../../../orders/components/Tables/OrderTableWithDetails.component';
import { mapOrderToTableRow } from '../../../orders/utils/map-order-to-table-row.util';
import { ORDER_TABLE_HEADERS } from '../../../orders/constants/order-table-headers.constant';
import { ORDER_TABLE_ROWS_PER_PAGE_OPTIONS } from '../../../orders/constants/order-table-pagination.constants';
import { ADMIN_USERS_APP_PATHS } from '../../constants/users-paths.constants';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',

    '& .MuiButton-root': {
      textTransform: 'none',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  createOrderButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

interface Props {
  user: User;
}

export default function UserOrdersTab({ user }: Props) {
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ORDER_TABLE_ROWS_PER_PAGE_OPTIONS[0]);

  const { loading, orders, totalCount } = useFindOrders({
    page: page + 1,
    limit: rowsPerPage,
    sortDirection: 'desc',
    filters: { ownerId: user.id },
  });

  const tableRows = useMemo(() => orders.map(mapOrderToTableRow), [orders]);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      {user.isActive && (
        <div className={classes.header}>
          <Button
            size="small"
            variant="contained"
            className={classes.createOrderButton}
            startIcon={<AddIcon />}
            onClick={() => history.push(ADMIN_USERS_APP_PATHS.USER_CREATE_ORDER(user.id))}
          >
            Créer une commande
          </Button>
        </div>
      )}
      <OrderTableWithDetails
        maxHeight="60svh"
        isLoading={loading}
        headers={ORDER_TABLE_HEADERS}
        orders={tableRows}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ORDER_TABLE_ROWS_PER_PAGE_OPTIONS}
        onPageChange={setPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </section>
  );
}
